import React, { useEffect, useState } from "react";
import { Button, Drawer, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiAirplane } from "@mdi/js";
import BrandCards, { BrandOption } from "./BrandCards";
import SheetHeaderClose from "./SheetHeaderClose";
import type { Tag } from "../../../../interfaces/tag.interface";
import type { ListingFilterState } from "../../../../utils/listingSearchParams";

interface Props {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  filters: ListingFilterState;
  tags: Tag[];
  brands: BrandOption[];
  onApply: (next: ListingFilterState) => void;
}

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

export default function FiltersPanel({ open, onClose, isMobile, filters, tags, brands, onApply }: Props) {
  const [draft, setDraft] = useState<ListingFilterState>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const guaranteed = draft.guarantees.includes("guarantee");

  const clearAll = () => setDraft({ ...draft, guarantees: [], tags: [], brands: [] });

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const body = (
    <div className="v2-filters">
      <section className="v2-filters__section">
        <h3 className="v2-filters__title">Recommendations</h3>
        <div className="v2-filters__chips">
          <button
            type="button"
            className={`v2-chip v2-chip--green ${guaranteed ? "is-active" : ""}`}
            onClick={() => setDraft({ ...draft, guarantees: toggle(draft.guarantees, "guarantee") })}
          >
            <Icon path={mdiAirplane} size="16px" />
            Guaranteed Departure
          </button>
        </div>
      </section>

      {tags.length > 0 && (
        <section className="v2-filters__section">
          <h3 className="v2-filters__title">Promotions</h3>
          <div className="v2-filters__chips">
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                title={tag.description}
                className={`v2-chip v2-chip--orange ${draft.tags.includes(tag.id) ? "is-active" : ""}`}
                onClick={() => setDraft({ ...draft, tags: toggle(draft.tags, tag.id) })}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </section>
      )}

      {brands.length > 0 && (
        <section className="v2-filters__section">
          <h3 className="v2-filters__title">Brand</h3>
          <BrandCards
            brands={brands}
            selected={draft.brands}
            variant={isMobile ? "stacked" : "row"}
            columns={isMobile ? 2 : 3}
            onToggle={(brand) => setDraft({ ...draft, brands: toggle(draft.brands, brand) })}
          />
        </section>
      )}
    </div>
  );

  const footer = (
    <div className="v2-filters__footer">
      <Button type="text" className="v2-filters__clear" onClick={clearAll}>
        {isMobile ? "Clear All" : "Clear"}
      </Button>
      <Button type="primary" className="v2-filters__apply" onClick={apply}>
        {isMobile ? "Apply" : "Show Results"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        placement="bottom"
        height="auto"
        title="Filters"
        closable={false}
        extra={<SheetHeaderClose onClose={onClose} />}
        rootClassName="v2-sheet v2-sheet--filters"
        footer={footer}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Filters"
      width={720}
      centered
      rootClassName="v2-filters-panel"
      footer={footer}
    >
      {body}
    </Modal>
  );
}
